import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent, type FormEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    listPeople,
    listHappenings,
    createPerson,
    updatePerson,
    deletePerson,
    deletePeople,
    summarizePerson,
    generateStaffTimetableLink,
    getHappeningsForPerson,
    checkPersonDependencies,
    checkBulkDependencies,
    getCompletionReminders,
    createCompletionReminder,
    deleteCompletionReminders,
    listPeopleParticipants,
    addPeopleParticipant,
    removePeopleParticipant,
    getCompletionNotificationRoles,
    setCompletionNotificationRoles,
    listUsers,
    listPersonRoles,
    setPersonRoles,
    listAvailability,
    setAvailability,
    type Person,
    type PersonCreate,
    type PersonUpdate,
    type PersonType,
    type Happening,
    type DependencyCheckResult,
    type CompletionReminder,
    type PeopleParticipant,
    type User,
} from '@littlebooker/dal';
import { InviteLinkButton } from '@/components/InviteLinkButton';
import { pickCounterpartShow, upcomingShowsFor } from '@/components/inviteLinks';
import { ImageFocusSelect } from '@/components/ImageFocusSelect';
import { PhotoUploadField } from '@/components/PhotoUploadField';
import { usePreferredImageRatio } from '@/hooks/usePreferredImageRatio';
import { notifyArtistsUpdated } from '@/lib/webhook';
import { MarkdownGuide } from '@/components/MarkdownGuide';
import { PhoneInput } from '@/components/PhoneInput';
import { useDefaultCountry } from '@/hooks/useDefaultCountry';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ViewToggle } from '@/components/ViewToggle';
import { SearchFilter } from '@/components/SearchFilter';
import { useViewStore } from '@/lib/viewStore';
import { useAuth } from '@/contexts/AuthContext';
import { DeleteConfirmationDialog } from '@/components/DeleteConfirmationDialog';
import { DangerZone } from '@/components/DangerZone';
import { RoleMultiSelect } from '@/components/staffing/RoleMultiSelect';
import { AvailabilityEditor, type AvailabilityWindow } from '@/components/staffing/AvailabilityEditor';
import { PlaceMultiSelect } from '@/components/staffing/PlaceMultiSelect';
import { AlertTriangle, ArrowLeft, ArrowRight, Bell, Calendar, Clock, Lock, Mail, Plus, Radio, Sparkles, Trash2, Users, X } from 'lucide-react';

type PersonFormState = {
    name: string;
    slug: string;
    pronouns: string;
    email: string;
    phone: string;
    bio: string;
    pictureUrl: string;
    imageFocus: string;
    instagram: string;
    soundcloud: string;
    bandcamp: string;
    customSocials: Array<{ id: string; label: string; url: string }>;
    isResident: boolean;
    isPublic: boolean;
    isCollective: boolean;
    inviteLinkRequested: boolean;
};

const PERSON_TYPE_META: Record<
    PersonType,
    {
        label: string;
        description: string;
        namePlaceholder: string;
        slugHint: string;
        helper: string;
    }
> = {
    artist: {
        label: 'Artist',
        description: 'Performers, DJs, bands, or collaborators you want to showcase publicly.',
        namePlaceholder: 'Artist / project name',
        slugHint: 'Used in artist URLs. Lowercase, friendly, auto-generated.',
        helper: 'Artists need more context so they can build their profile later.',
    },
    staff: {
        label: 'Staff',
        description: 'Internal crew members, residents, and people helping run the space.',
        namePlaceholder: 'Staff member name',
        slugHint: 'Used internally so we can reference staff in programming.',
        helper: 'No public profile, just enough to track who is helping out.',
    },
    guest: {
        label: 'Guest',
        description: 'One-off collaborators, hosts, or partners visiting the station.',
        namePlaceholder: 'Guest name',
        slugHint: 'Slug only matters if they become recurring collaborators.',
        helper: 'Great for visiting hosts or community partners.',
    },
    support: {
        label: 'Support',
        description: 'Vendors, supporters, or organizations adjacent to your programming.',
        namePlaceholder: 'Organization / support name',
        slugHint: 'Used to tag assets or bookings to the right partner.',
        helper: 'Keep track of technical or marketing partners.',
    },
};

const ARTIST_CATEGORY_OPTIONS = [
    {
        value: false,
        label: 'Individual Artist',
        description: 'Solo performers, DJs, or individual creators.',
    },
    {
        value: true,
        label: 'Collective',
        description: 'Groups, bands, crews, or collaborative projects.',
    },
];

const SLUG_REGEX = /[^a-z0-9-]/g;

const slugify = (value: string) =>
    value
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .replace(/-{2,}/g, '-');

const emptySocialRow = () => ({ id: Math.random().toString(36).slice(2, 9), label: '', url: '' });

const defaultFormState = (): PersonFormState => ({
    name: '',
    slug: '',
    pronouns: '',
    email: '',
    phone: '',
    bio: '',
    pictureUrl: '',
    imageFocus: 'center',
    instagram: '',
    soundcloud: '',
    bandcamp: '',
    customSocials: [emptySocialRow()],
    isResident: false,
    isPublic: false,
    isCollective: false,
    inviteLinkRequested: true,
});

const valueOrNull = (value: string) => {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
};

const isValidUrl = (url: string): boolean => {
    if (!url || !url.trim()) return true; // Empty is valid (optional field)
    const trimmed = url.trim();
    // Check if it's just a username (starts with @ or has no protocol/domain)
    if (trimmed.startsWith('@') || (!trimmed.includes('.') && !trimmed.includes('/'))) {
        return false;
    }
    // Check if it looks like a URL (has protocol or domain)
    return trimmed.startsWith('http://') || trimmed.startsWith('https://') || trimmed.includes('.');
};

const buildSocials = (form: PersonFormState) => ({
    instagram: valueOrNull(form.instagram),
    soundcloud: valueOrNull(form.soundcloud),
    bandcamp: valueOrNull(form.bandcamp),
    custom: form.customSocials
        .filter((link) => link.label.trim() && link.url.trim())
        .map((link) => ({ label: link.label.trim(), url: link.url.trim() })),
});

const mapPersonToFormState = (person: Person): PersonFormState => ({
    name: person.name ?? '',
    slug: person.slug,
    pronouns: person.pronouns ?? '',
    email: person.email,
    phone: person.phone ?? '',
    bio: person.bio ?? '',
    pictureUrl: person.picture_url ?? '',
    imageFocus: person.image_focus ?? 'center',
    instagram: person.socials?.instagram ?? '',
    soundcloud: person.socials?.soundcloud ?? '',
    bandcamp: person.socials?.bandcamp ?? '',
    customSocials: (person.socials?.custom ?? []).map((link) => ({
        id: Math.random().toString(36).slice(2, 9),
        label: link.label,
        url: link.url,
    })),
    isResident: person.is_resident ?? false,
    isPublic: person.is_public ?? false,
    isCollective: person.is_collective ?? false,
    // When editing, default to false - only generate new link if user explicitly checks the box
    // This prevents accidentally regenerating tokens and invalidating existing invite links
    inviteLinkRequested: false,
});

export default function PeoplePage() {
    const queryClient = useQueryClient();
    const location = useLocation();
    const navigate = useNavigate();
    const defaultCountry = useDefaultCountry();
    const preferredImageRatio = usePreferredImageRatio();
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogStep, setDialogStep] = useState<'type' | 'form'>('type');
    const [selectedType, setSelectedType] = useState<PersonType>('artist');
    const [formState, setFormState] = useState<PersonFormState>(() => defaultFormState());
    const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
    const [urlErrors, setUrlErrors] = useState<Record<string, string>>({});
    const [imageMismatched, setImageMismatched] = useState(false);
    const [editingPerson, setEditingPerson] = useState<Person | null>(null);
    const [showMarkdownGuide, setShowMarkdownGuide] = useState(false);
    const [copiedInvitePersonId, setCopiedInvitePersonId] = useState<string | null>(null);
    const [copiedStaffLinkPersonId, setCopiedStaffLinkPersonId] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const viewMode = useViewStore((state) => state.getViewMode('people'));
    const setViewMode = useViewStore((state) => state.setViewMode);

    // Delete functionality
    const { user } = useAuth();
    const canDelete = !user || user.role === 'owner' || user.role === 'admin';
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deletingPerson, setDeletingPerson] = useState<Person | null>(null);
    const [deleteDependencies, setDeleteDependencies] = useState<DependencyCheckResult | null>(null);
    const [selectedPersonIds, setSelectedPersonIds] = useState<Set<string>>(new Set());
    const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
    const [bulkDeleteDependencies, setBulkDeleteDependencies] = useState<Map<string, DependencyCheckResult>>(new Map());
    const [checkingSingleDependencies, setCheckingSingleDependencies] = useState<Set<string>>(new Set());
    const [checkingBulkDependencies, setCheckingBulkDependencies] = useState(false);
    const [completionReminders, setCompletionReminders] = useState<Array<{ id?: string; offset_hours: number }>>([]);
    const loadedRemindersForPersonId = useRef<string | null>(null);
    const remindersInitializedForPersonId = useRef<string | null>(null);
    const closeDialogTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    // Tracks the ?edit=<id> target we've already opened, so closing the dialog
    // doesn't reopen it (the URL param can linger because router navigation runs
    // in a low-priority transition and may not clear before the dialog closes).
    const consumedEditIdRef = useRef<string | null>(null);
    const [notifyRoles, setNotifyRoles] = useState<string[]>([]);
    const [newParticipantUserId, setNewParticipantUserId] = useState<string>('');
    const [newParticipantRole, setNewParticipantRole] = useState<string>('');
    // Pending participants for creation (before person exists in DB)
    const [pendingParticipants, setPendingParticipants] = useState<Array<{ odempotencyKey: string; user_id: string; user_name: string; role: string }>>([]);
    // Staffing: role qualifications + scheduling constraints (staff/support only)
    const [staffRoleIds, setStaffRoleIds] = useState<string[]>([]);
    const [staffConstraints, setStaffConstraints] = useState<{ maxConsecutive: string; maxDaily: string; minBreak: string }>({ maxConsecutive: '', maxDaily: '', minBreak: '' });
    const [allowedPlaceIds, setAllowedPlaceIds] = useState<string[]>([]);
    const [availabilityWindows, setAvailabilityWindows] = useState<AvailabilityWindow[]>([]);

    // Load completion reminders when editing a person
    const { data: existingCompletionReminders = [], isFetched: completionRemindersFetched } = useQuery({
        queryKey: ['completion-reminders', editingPerson?.id, 'profile'],
        queryFn: () => editingPerson ? getCompletionReminders('profile', editingPerson.id) : Promise.resolve([]),
        enabled: !!editingPerson && editingPerson.type === 'artist',
    });

    // Load staff users for participant assignment
    const { data: allUsers = [] } = useQuery({
        queryKey: ['users', { is_active: true }],
        queryFn: () => listUsers({ is_active: true }),
        enabled: dialogOpen && selectedType === 'artist',
    });

    // Load participants for the editing person
    const { data: personParticipants = [], refetch: refetchParticipants } = useQuery({
        queryKey: ['people-participants', editingPerson?.id],
        queryFn: () => editingPerson ? listPeopleParticipants(editingPerson.id) : Promise.resolve([]),
        enabled: !!editingPerson && editingPerson.type === 'artist',
    });

    // Load role qualifications for the editing staff/support person
    const isStaffType = selectedType === 'staff' || selectedType === 'support';
    const { data: loadedPersonRoles = [] } = useQuery({
        queryKey: ['person-roles', editingPerson?.id],
        queryFn: () => (editingPerson ? listPersonRoles(editingPerson.id) : Promise.resolve([])),
        enabled: !!editingPerson && (editingPerson.type === 'staff' || editingPerson.type === 'support'),
    });

    useEffect(() => {
        if (editingPerson) setStaffRoleIds(loadedPersonRoles.map((r) => r.id));
    }, [loadedPersonRoles, editingPerson?.id]);

    // Load general availability for the editing staff/support person.
    const { data: loadedAvailability = [] } = useQuery({
        queryKey: ['staff-availability', editingPerson?.id],
        queryFn: () => (editingPerson ? listAvailability({ personId: editingPerson.id, programId: null }) : Promise.resolve([])),
        enabled: !!editingPerson && (editingPerson.type === 'staff' || editingPerson.type === 'support'),
    });

    useEffect(() => {
        if (editingPerson) {
            setAvailabilityWindows(loadedAvailability.map((w) => ({ start_time: w.start_time, end_time: w.end_time })));
        }
    }, [loadedAvailability, editingPerson?.id]);

    // Load notification roles for the editing person
    const { data: existingNotificationRoles = [], isFetched: notificationRolesFetched } = useQuery({
        queryKey: ['completion-notification-roles', 'person', editingPerson?.id],
        queryFn: () => editingPerson ? getCompletionNotificationRoles('person', editingPerson.id) : Promise.resolve([]),
        enabled: !!editingPerson && editingPerson.type === 'artist',
    });

    // Initialize notification roles when loaded
    useEffect(() => {
        if (notificationRolesFetched && editingPerson) {
            setNotifyRoles(existingNotificationRoles.map(r => r.notify_role));
        }
    }, [notificationRolesFetched, editingPerson?.id, existingNotificationRoles]);

    // Reset notification roles and pending participants when person changes
    useEffect(() => {
        if (!editingPerson) {
            setNotifyRoles([]);
            setNewParticipantUserId('');
            setNewParticipantRole('');
            setPendingParticipants([]);
            setStaffRoleIds([]);
            setStaffConstraints({ maxConsecutive: '', maxDaily: '', minBreak: '' });
            setAllowedPlaceIds([]);
            setAvailabilityWindows([]);
        }
    }, [editingPerson?.id]);

    // Combined view of participants (pending for creation, from DB for editing)
    const displayParticipants = useMemo(() => {
        if (editingPerson) {
            return personParticipants.map(p => ({
                id: p.id,
                user_id: p.user_id,
                user_name: p.user_name || 'Unknown',
                role: p.role,
            }));
        }
        return pendingParticipants.map(p => ({
            id: p.odempotencyKey,
            user_id: p.user_id,
            user_name: p.user_name,
            role: p.role,
        }));
    }, [editingPerson, personParticipants, pendingParticipants]);

    // Get unique roles from participants
    const uniqueParticipantRoles = useMemo(() => {
        return Array.from(new Set(displayParticipants.map(p => p.role)));
    }, [displayParticipants]);

    // Reset reminders when person changes
    useEffect(() => {
        if (!editingPerson) {
            setCompletionReminders([]);
            loadedRemindersForPersonId.current = null;
            remindersInitializedForPersonId.current = null;
            return;
        }

        const currentPersonId = editingPerson.id;
        if (loadedRemindersForPersonId.current !== currentPersonId) {
            loadedRemindersForPersonId.current = currentPersonId;
            remindersInitializedForPersonId.current = null;
            setCompletionReminders([]);
        }
    }, [editingPerson?.id]);

    // Load reminders when query completes
    useEffect(() => {
        if (!editingPerson || loadedRemindersForPersonId.current !== editingPerson.id) {
            return;
        }

        if (completionRemindersFetched && remindersInitializedForPersonId.current !== editingPerson.id) {
            remindersInitializedForPersonId.current = editingPerson.id;
            // Only this person's own schedule. Show-linked rows belong to that show's dialog, and
            // offset 0 rows are one-off sends; listing them here meant a save rewrote them all.
            setCompletionReminders(
                existingCompletionReminders
                    .filter(r => !r.happening_id && r.offset_hours !== 0)
                    .map(r => ({ id: r.id, offset_hours: r.offset_hours })),
            );
        }
    }, [editingPerson?.id, completionRemindersFetched, existingCompletionReminders]);

    const { data: people = [], isLoading } = useQuery({
        queryKey: ['people'],
        queryFn: () => listPeople({}),
    });

    /**
     * Upcoming shows, so every card and row can offer a combined invite link rather than a
     * profile-only one. Fetched once for the page: `listHappenings` already embeds
     * `happening_participants`, so one request maps every artist to their next show — a
     * per-row lookup would be an N+1 on a list that routinely runs to hundreds of people.
     */
    const { data: upcomingShows = [] } = useQuery({
        queryKey: ['upcoming-shows-with-stub'],
        queryFn: () =>
            listHappenings({
                type: 'show',
                start_time_gte: new Date().toISOString(),
                order: 'asc',
                limit: 300,
            }),
    });

    /** person id -> that person's upcoming shows, soonest first. */
    const upcomingShowsByPerson = useMemo(() => {
        const map = new Map<string, Happening[]>();
        for (const happening of upcomingShows) {
            const participants = (happening as Happening & {
                happening_participants?: Array<{ person_id: string | null }>;
            }).happening_participants ?? [];
            for (const participant of participants) {
                if (!participant.person_id) continue;
                const existing = map.get(participant.person_id);
                if (existing) {
                    existing.push(happening);
                } else {
                    map.set(participant.person_id, [happening]);
                }
            }
        }
        return map;
    }, [upcomingShows]);

    /** The show a given person's invite link should point at, or null to fall back to profile-only. */
    const counterpartShowFor = useCallback(
        (personId: string) => pickCounterpartShow(upcomingShowsByPerson.get(personId) ?? []),
        [upcomingShowsByPerson],
    );

    // Fetch happenings for the currently editing person (backlinks)
    const { data: personHappenings = [] } = useQuery({
        queryKey: ['happeningsForPerson', editingPerson?.id],
        queryFn: () => getHappeningsForPerson(editingPerson!.id),
        enabled: !!editingPerson,
    });

    // The show the dialog's invite link points at. Reuses the backlinks query above so the
    // combined link costs no extra fetch. Null when the artist has nothing upcoming — the
    // button then degrades to a profile-only link rather than linking a past show.
    const dialogCounterpartShow = useMemo(
        () => pickCounterpartShow(personHappenings),
        [personHappenings],
    );

    // The artist's other upcoming shows, so a programmer chasing a specific date can pick
    // it rather than assuming the nearest one.
    const dialogShowAlternates = useMemo(
        () =>
            upcomingShowsFor(personHappenings)
                .filter((happening) => happening.id !== dialogCounterpartShow?.id)
                .map((happening) => ({
                    person: editingPerson,
                    happening,
                    label: `Invite link for ${happening.title}`,
                })),
        [personHappenings, dialogCounterpartShow, editingPerson],
    );

    // Handle deep-link from global search - open entity directly
    useEffect(() => {
        const state = location.state as { openEntityId?: string; entityType?: string } | null;
        if (state?.openEntityId && state?.entityType === 'person' && people.length > 0) {
            const person = people.find((p) => p.id === state.openEntityId);
            if (person) {
                // Cancel any pending closeDialog timeout to prevent it from
                // clearing the state we're about to set (race condition)
                if (closeDialogTimeoutRef.current) {
                    clearTimeout(closeDialogTimeoutRef.current);
                    closeDialogTimeoutRef.current = null;
                }
                // Open the edit dialog for this person
                setEditingPerson(person);
                setSelectedType(person.type);
                setFormState(mapPersonToFormState(person));
                setSlugManuallyEdited(true);
                setDialogStep('form');
                setDialogOpen(true);
                // Clear the state so it doesn't re-trigger
                navigate(location.pathname, { replace: true, state: null });
            }
        }
    }, [location.state, people, navigate, location.pathname]);

    // Handle deep-link from URL query parameter (e.g., /people?edit=<id>)
    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const personId = searchParams.get('edit');

        // No deep-link target: reset so a future ?edit=<id> is honored again.
        if (!personId) {
            consumedEditIdRef.current = null;
            return;
        }
        // Already opened this target — don't reopen it after the user closes the
        // dialog (which resets editingPerson and re-runs this effect).
        if (consumedEditIdRef.current === personId || people.length === 0) {
            return;
        }

        const person = people.find((p) => p.id === personId);
        if (person) {
            consumedEditIdRef.current = personId;
            if (closeDialogTimeoutRef.current) {
                clearTimeout(closeDialogTimeoutRef.current);
                closeDialogTimeoutRef.current = null;
            }
            setEditingPerson(person);
            setSelectedType(person.type);
            setFormState(mapPersonToFormState(person));
            setSlugManuallyEdited(true);
            setDialogStep('form');
            setDialogOpen(true);
            // Best-effort: clear the query param so a refresh doesn't reopen.
            navigate(location.pathname, { replace: true });
        }
    }, [location.search, people, navigate, location.pathname]);

    // Filter people based on search query
    const filteredPeople = useMemo(() => {
        if (!searchQuery.trim()) return people;
        const lowerQuery = searchQuery.toLowerCase();
        return people.filter(
            (person) =>
                person.name?.toLowerCase().includes(lowerQuery) ||
                person.slug?.toLowerCase().includes(lowerQuery) ||
                person.email?.toLowerCase().includes(lowerQuery) ||
                person.type?.toLowerCase().includes(lowerQuery)
        );
    }, [people, searchQuery]);

    const handleSaveCompletionReminders = async (personId: string, personEmail: string, personName: string) => {
        /*
         * Touch only this person's own timed reminders: not show-linked ones (each show's
         * dialog owns those), not one-off sends, and not ones already sent. Recreating a sent
         * row cleared sent_at, and a reminder sent within the last hour is still inside the
         * scheduler's send window — so saving the person emailed them again.
         */
        const scope = { keepImmediate: true, keepSent: true, unlinkedOnly: true };
        const wanted = selectedType === 'artist' && personEmail ? completionReminders : [];

        try {
            await deleteCompletionReminders('profile', personId, scope);
            const alreadySent = new Set(
                (await getCompletionReminders('profile', personId))
                    .filter(r => !r.happening_id && r.offset_hours !== 0)
                    .map(r => r.offset_hours),
            );

            for (const reminder of wanted) {
                if (alreadySent.has(reminder.offset_hours)) continue;
                await createCompletionReminder({
                    reminder_type: 'profile',
                    entity_id: personId,
                    recipient_email: personEmail,
                    recipient_name: personName,
                    offset_hours: reminder.offset_hours,
                    happening_id: null,
                });
            }

            queryClient.invalidateQueries({ queryKey: ['completion-reminders', personId, 'profile'] });
        } catch (error) {
            console.error('Failed to save completion reminders:', error);
            // Don't throw - let the form submission succeed even if reminders fail
        }
    };

    const handleSaveNotificationRoles = async (personId: string, accountId: string) => {
        // Only save notification roles for artists with participants
        if (selectedType !== 'artist') {
            return;
        }

        try {
            await setCompletionNotificationRoles('person', personId, notifyRoles, accountId);
            queryClient.invalidateQueries({ queryKey: ['completion-notification-roles', 'person', personId] });
        } catch (error) {
            console.error('Failed to save notification roles:', error);
        }
    };

    const handleAddParticipant = async () => {
        if (!newParticipantUserId || !newParticipantRole.trim()) {
            return;
        }

        const role = newParticipantRole.trim().toLowerCase().replace(/\s+/g, '_');
        const selectedUser = allUsers.find(u => u.id === newParticipantUserId);
        const userName = selectedUser?.name || 'Unknown';

        if (editingPerson) {
            // When editing, save directly to DB
            try {
                await addPeopleParticipant({
                    person_id: editingPerson.id,
                    user_id: newParticipantUserId,
                    role,
                    account_id: editingPerson.account_id,
                });
                await refetchParticipants();
            } catch (error) {
                console.error('Failed to add participant:', error);
                return;
            }
        } else {
            // When creating, add to pending list
            setPendingParticipants(prev => [
                ...prev,
                {
                    odempotencyKey: `${newParticipantUserId}-${role}-${Date.now()}`,
                    user_id: newParticipantUserId,
                    user_name: userName,
                    role,
                },
            ]);
        }

        setNewParticipantUserId('');
        setNewParticipantRole('');
    };

    const handleRemoveParticipant = async (participantId: string) => {
        if (editingPerson) {
            // When editing, remove from DB
            try {
                await removePeopleParticipant(participantId);
                await refetchParticipants();
            } catch (error) {
                console.error('Failed to remove participant:', error);
            }
        } else {
            // When creating, remove from pending list
            setPendingParticipants(prev => prev.filter(p => p.odempotencyKey !== participantId));
        }
    };

    const createMutation = useMutation({
        mutationFn: (data: PersonCreate) => createPerson(data),
        onSuccess: async (newPerson) => {
            queryClient.invalidateQueries({ queryKey: ['people'] });

            // Save role qualifications + availability for staff/support
            if ((selectedType === 'staff' || selectedType === 'support') && newPerson.id) {
                try {
                    await setPersonRoles(newPerson.id, staffRoleIds);
                    await setAvailability(newPerson.id, availabilityWindows, { programId: null });
                } catch (error) {
                    console.error('Failed to save staff roles/availability:', error);
                }
                queryClient.invalidateQueries({ queryKey: ['person-roles', newPerson.id] });
                queryClient.invalidateQueries({ queryKey: ['staff-availability', newPerson.id] });
            }

            // Save pending participants for this new person
            if (selectedType === 'artist' && pendingParticipants.length > 0 && newPerson.id && newPerson.account_id) {
                for (const pending of pendingParticipants) {
                    try {
                        await addPeopleParticipant({
                            person_id: newPerson.id,
                            user_id: pending.user_id,
                            role: pending.role,
                            account_id: newPerson.account_id,
                        });
                    } catch (error) {
                        console.error('Failed to add participant:', error);
                    }
                }
                queryClient.invalidateQueries({ queryKey: ['people-participants', newPerson.id] });

                // Save notification roles after participants are created
                await handleSaveNotificationRoles(newPerson.id, newPerson.account_id);
            }

            // Save completion reminders if this is an artist with email
            if (selectedType === 'artist' && formState.email && newPerson.id) {
                await handleSaveCompletionReminders(newPerson.id, formState.email, newPerson.name);
            }

            // Notify website to revalidate if this is an artist
            if (selectedType === 'artist') {
                notifyArtistsUpdated(newPerson.slug);
            }

            closeDialog();
        },
    });

    const updateMutation = useMutation({
        mutationFn: ({ id, patch }: { id: string; patch: PersonUpdate }) => updatePerson(id, patch),
        onSuccess: async (updatedPerson) => {
            queryClient.invalidateQueries({ queryKey: ['people'] });
            // Save role qualifications + availability for staff/support
            if ((selectedType === 'staff' || selectedType === 'support') && updatedPerson.id) {
                try {
                    await setPersonRoles(updatedPerson.id, staffRoleIds);
                    await setAvailability(updatedPerson.id, availabilityWindows, { programId: null });
                } catch (error) {
                    console.error('Failed to save staff roles/availability:', error);
                }
                queryClient.invalidateQueries({ queryKey: ['person-roles', updatedPerson.id] });
                queryClient.invalidateQueries({ queryKey: ['staff-availability', updatedPerson.id] });
            }
            // Save completion reminders if this is an artist with email
            if (selectedType === 'artist' && (formState.email || updatedPerson.email) && updatedPerson.id) {
                const email = formState.email || updatedPerson.email || '';
                const name = updatedPerson.name;
                if (email) {
                    await handleSaveCompletionReminders(updatedPerson.id, email, name);
                }
            }
            // Save notification roles if this is an artist
            if (selectedType === 'artist' && updatedPerson.id && updatedPerson.account_id) {
                await handleSaveNotificationRoles(updatedPerson.id, updatedPerson.account_id);
            }

            // Notify website to revalidate if this is an artist
            if (selectedType === 'artist') {
                notifyArtistsUpdated(updatedPerson.slug);
            }

            closeDialog();
        },
    });

    // Delete mutations
    const deleteMutation = useMutation({
        mutationFn: (id: string) => deletePerson(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['people'] });
            queryClient.invalidateQueries({ queryKey: ['happenings'] });
            queryClient.invalidateQueries({ queryKey: ['tasks'] });
            // Notify website to revalidate if this was an artist
            if (deletingPerson?.type === 'artist') {
                notifyArtistsUpdated();
            }
            setDeleteDialogOpen(false);
            setDeletingPerson(null);
            setDeleteDependencies(null);
            setEditingPerson(null);
        },
    });

    const bulkDeleteMutation = useMutation({
        mutationFn: (ids: string[]) => deletePeople(ids),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['people'] });
            queryClient.invalidateQueries({ queryKey: ['happenings'] });
            queryClient.invalidateQueries({ queryKey: ['tasks'] });
            // Notify website to revalidate (might have included artists)
            notifyArtistsUpdated();
            setBulkDeleteDialogOpen(false);
            setSelectedPersonIds(new Set());
            setBulkDeleteDependencies(new Map());
        },
        onError: (error) => {
            console.error('Bulk delete failed:', error);
            alert(`Failed to delete people: ${error instanceof Error ? error.message : 'Unknown error'}`);
        },
    });

    // Delete handlers
    const handleDeleteClick = async (person: Person, e?: React.MouseEvent) => {
        if (e) {
            e.stopPropagation();
        }
        if (!canDelete) return;

        setCheckingSingleDependencies((prev) => new Set(prev).add(person.id));
        try {
            setDeletingPerson(person);
            const dependencies = await checkPersonDependencies(person.id);
            setDeleteDependencies(dependencies);
            setDeleteDialogOpen(true);
        } finally {
            setCheckingSingleDependencies((prev) => {
                const next = new Set(prev);
                next.delete(person.id);
                return next;
            });
        }
    };

    const handleDeleteConfirm = () => {
        if (deletingPerson) {
            deleteMutation.mutate(deletingPerson.id);
        }
    };

    const handleBulkDeleteClick = async () => {
        if (selectedPersonIds.size === 0 || !canDelete) return;

        setCheckingBulkDependencies(true);
        try {
            const ids = Array.from(selectedPersonIds);
            const dependencies = await checkBulkDependencies('person', ids);
            setBulkDeleteDependencies(dependencies);
            setBulkDeleteDialogOpen(true);
        } finally {
            setCheckingBulkDependencies(false);
        }
    };

    const handleBulkDeleteConfirm = () => {
        if (selectedPersonIds.size > 0) {
            bulkDeleteMutation.mutate(Array.from(selectedPersonIds));
        }
    };

    const togglePersonSelection = (personId: string, e?: React.MouseEvent) => {
        if (e) {
            e.stopPropagation();
        }
        setSelectedPersonIds((prev) => {
            const next = new Set(prev);
            if (next.has(personId)) {
                next.delete(personId);
            } else {
                next.add(personId);
            }
            return next;
        });
    };

    const toggleSelectAll = () => {
        if (selectedPersonIds.size === filteredPeople.length) {
            setSelectedPersonIds(new Set());
        } else {
            setSelectedPersonIds(new Set(filteredPeople.map((p) => p.id)));
        }
    };

    const typeMeta = PERSON_TYPE_META[selectedType];
    const isArtist = selectedType === 'artist';
    const isSaving = createMutation.isPending || updateMutation.isPending;
    const mutationError = (createMutation.error ?? updateMutation.error) as Error | null;
    const photoIdentifier = editingPerson?.slug || formState.slug || formState.name;

    const startCreateFlow = () => {
        setEditingPerson(null);
        setSelectedType('artist');
        setFormState(defaultFormState());
        setSlugManuallyEdited(false);
        setDialogStep('type');
        setDialogOpen(true);
    };

    const closeDialog = () => {
        setDialogOpen(false);
        if (closeDialogTimeoutRef.current) {
            clearTimeout(closeDialogTimeoutRef.current);
        }
        closeDialogTimeoutRef.current = setTimeout(() => {
            closeDialogTimeoutRef.current = null;
            setDialogStep('type');
            setEditingPerson(null);
            setFormState(defaultFormState());
            setSlugManuallyEdited(false);
        }, 200);
    };

    const handleTypeContinue = () => {
        setDialogStep('form');
    };

    const handleFieldChange =
        (field: keyof PersonFormState) => (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            const { value } = event.target;
            setFormState((prev) => {
                if (field === 'name' && !slugManuallyEdited) {
                    // When name changes, use it for slug, or fall back to email
                    const newSlug = value ? slugify(value) : (prev.email ? slugify(prev.email.split('@')[0]) : '');
                    return {
                        ...prev,
                        name: value,
                        slug: newSlug,
                    };
                }
                if (field === 'email' && !slugManuallyEdited && !prev.name) {
                    // When email changes and name is empty, use email for slug
                    const newSlug = value ? slugify(value.split('@')[0]) : '';
                    return {
                        ...prev,
                        email: value,
                        slug: newSlug,
                    };
                }
                if (field === 'slug') {
                    setSlugManuallyEdited(true);
                    return { ...prev, slug: value.replace(SLUG_REGEX, '') };
                }
                return { ...prev, [field]: value };
            });
        };

    const handleRegenerateSlug = () => {
        setFormState((prev) => {
            const newSlug = prev.name ? slugify(prev.name) : (prev.email ? slugify(prev.email.split('@')[0]) : '');
            return { ...prev, slug: newSlug };
        });
        setSlugManuallyEdited(false);
    };

    const handleCustomSocialChange = (id: string, field: 'label' | 'url', value: string) => {
        setFormState((prev) => ({
            ...prev,
            customSocials: prev.customSocials.map((row) => (row.id === id ? { ...row, [field]: value } : row)),
        }));

        // Validate custom social URLs
        if (field === 'url') {
            if (value && !isValidUrl(value)) {
                setUrlErrors((prev) => ({ ...prev, [`custom_${id}`]: 'Please enter a full URL (e.g., https://example.com), not just a username' }));
            } else {
                setUrlErrors((prev) => ({ ...prev, [`custom_${id}`]: '' }));
            }
        }
    };

    const addCustomSocialRow = () => {
        setFormState((prev) => ({
            ...prev,
            customSocials: [...prev.customSocials, emptySocialRow()],
        }));
    };

    const removeCustomSocialRow = (id: string) => {
        setFormState((prev) => ({
            ...prev,
            customSocials: prev.customSocials.length === 1 ? prev.customSocials : prev.customSocials.filter((row) => row.id !== id),
        }));
    };

    const handleInviteToggle = (event: ChangeEvent<HTMLInputElement>) => {
        setFormState((prev) => ({ ...prev, inviteLinkRequested: event.target.checked }));
    };

    const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        // Validate: Email is required
        if (!formState.email.trim()) {
            return; // Don't submit - email is required
        }

        // Validate: Artists require at least one staff member and one notification role
        if (selectedType === 'artist') {
            if (displayParticipants.length === 0) {
                return; // Don't submit - UI already shows error state
            }
            if (notifyRoles.length === 0) {
                return; // Don't submit - UI already shows error state
            }
        }

        // Staff/support: persist scheduling constraints (stored in metadata.staffing by the DAL).
        const staffingEntries = isStaffType
            ? {
                  ...(staffConstraints.maxConsecutive ? { max_consecutive_hours: Number(staffConstraints.maxConsecutive) } : {}),
                  ...(staffConstraints.maxDaily ? { max_daily_hours: Number(staffConstraints.maxDaily) } : {}),
                  ...(staffConstraints.minBreak ? { min_break_minutes: Number(staffConstraints.minBreak) } : {}),
                  ...(allowedPlaceIds.length > 0 ? { allowed_place_ids: allowedPlaceIds } : {}),
              }
            : null;
        const staffingPatch = isStaffType ? { staffing: staffingEntries } : {};

        const payload = {
            name: valueOrNull(formState.name),
            slug: formState.slug.trim() || undefined,
            type: selectedType,
            pronouns: valueOrNull(formState.pronouns),
            email: formState.email.trim(),
            phone: valueOrNull(formState.phone),
            bio: valueOrNull(formState.bio),
            picture_url: valueOrNull(formState.pictureUrl),
            image_focus: formState.imageFocus || null,
            socials: buildSocials(formState),
            is_resident: formState.isResident,
            is_public: formState.isPublic,
            is_collective: formState.isCollective,
            ...staffingPatch,
            // Only include generateInviteLink if explicitly requested
            // When editing, this prevents accidentally regenerating existing tokens
            ...(formState.inviteLinkRequested ? { generateInviteLink: true } : {}),
        };

        if (editingPerson) {
            updateMutation.mutate({ id: editingPerson.id, patch: payload });
            return;
        }
        createMutation.mutate(payload);
    };

    const openEditFlow = (person: Person) => {
        setEditingPerson(person);
        setSelectedType(person.type);
        setFormState(mapPersonToFormState(person));
        const staffing = person.staffing;
        setStaffConstraints({
            maxConsecutive: staffing?.max_consecutive_hours != null ? String(staffing.max_consecutive_hours) : '',
            maxDaily: staffing?.max_daily_hours != null ? String(staffing.max_daily_hours) : '',
            minBreak: staffing?.min_break_minutes != null ? String(staffing.min_break_minutes) : '',
        });
        setAllowedPlaceIds(staffing?.allowed_place_ids ?? []);
        setSlugManuallyEdited(true);
        setDialogStep('form');
        setDialogOpen(true);
    };

    // Copy a staff member's personal timetable link. Generates an invite token
    // on the fly if the person doesn't have one yet (same token the artist
    // invite flow uses, but pointed at the read-only staff schedule view).
    const handleCopyStaffLink = async (person: Person) => {
        const origin = typeof window !== 'undefined' ? window.location.origin : '';
        try {
            let target = person;
            if (!target.invite_token) {
                target = await updatePerson(person.id, { generateInviteLink: true });
                queryClient.invalidateQueries({ queryKey: ['people'] });
            }
            const link = generateStaffTimetableLink(target, origin);
            if (!link) {
                return;
            }
            if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
                await navigator.clipboard.writeText(link);
            } else if (typeof window !== 'undefined') {
                window.prompt('Copy staff timetable link', link);
            }
            setCopiedStaffLinkPersonId(person.id);
            setTimeout(() => setCopiedStaffLinkPersonId(null), 2000);
        } catch (error) {
            console.error('Failed to copy staff timetable link', error);
        }
    };

    const peopleWithSummaries = useMemo(
        () =>
            filteredPeople.map((person) => ({
                person,
                summary: summarizePerson(person),
            })),
        [filteredPeople],
    );

    return (
        <div className="space-y-8">
            <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                    <p className="text-sm uppercase tracking-wide text-muted-foreground">Roster</p>
                    <h1 className="text-3xl font-bold">People</h1>
                    <p className="text-muted-foreground">
                        Create profiles for artists, performers and guests.
                    </p>
                </div>
                <Button onClick={startCreateFlow}>
                    <Plus className="mr-2 h-4 w-4" />
                    Add Person
                </Button>
            </div>

            <div className="flex flex-wrap items-center gap-4">
                <SearchFilter
                    value={searchQuery}
                    onChange={setSearchQuery}
                    placeholder="Search people by name, slug, email, or type..."
                    className="w-full max-w-sm"
                />
                <ViewToggle
                    view={viewMode}
                    onViewChange={(mode) => setViewMode('people', mode)}
                    availableViews={['list', 'card']}
                />
                {searchQuery && (
                    <span className="text-sm text-muted-foreground">
                        {filteredPeople.length} of {people.length} people
                    </span>
                )}
            </div>

            {/* Bulk actions bar */}
            {selectedPersonIds.size > 0 && canDelete && (
                <div className="flex items-center justify-between rounded-lg border bg-muted/50 p-3">
                    <span className="text-sm font-medium">
                        {selectedPersonIds.size} person{selectedPersonIds.size !== 1 ? 's' : ''} selected
                    </span>
                    <div className="flex items-center gap-2">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setSelectedPersonIds(new Set())}
                            disabled={checkingBulkDependencies}
                        >
                            Clear selection
                        </Button>
                        <Button
                            variant="destructive"
                            size="sm"
                            onClick={handleBulkDeleteClick}
                            disabled={checkingBulkDependencies}
                        >
                            {checkingBulkDependencies ? (
                                <>
                                    <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                                    Checking dependencies...
                                </>
                            ) : (
                                <>
                                    <Trash2 className="mr-2 h-4 w-4" />
                                    Delete {selectedPersonIds.size}
                                </>
                            )}
                        </Button>
                    </div>
                </div>
            )}

            {isLoading ? (
                <p>Loading...</p>
            ) : viewMode === 'card' ? (
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {peopleWithSummaries.map(({ person, summary }) => (
                        <Card
                            key={person.id}
                            role="button"
                            tabIndex={0}
                            onClick={() => openEditFlow(person)}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter' || event.key === ' ') {
                                    event.preventDefault();
                                    openEditFlow(person);
                                }
                            }}
                            className="cursor-pointer border-muted transition hover:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary relative"
                        >
                            {canDelete && (
                                <div className="absolute top-2 right-2 z-10 flex items-center gap-2">
                                    <input
                                        type="checkbox"
                                        checked={selectedPersonIds.has(person.id)}
                                        onChange={() => togglePersonSelection(person.id)}
                                        onClick={(e) => e.stopPropagation()}
                                        className="h-4 w-4 rounded border-gray-300"
                                        disabled={checkingSingleDependencies.has(person.id)}
                                    />
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={(e) => handleDeleteClick(person, e)}
                                        className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                                        disabled={checkingSingleDependencies.has(person.id)}
                                        title={checkingSingleDependencies.has(person.id) ? 'Checking dependencies...' : 'Delete'}
                                    >
                                        {checkingSingleDependencies.has(person.id) ? (
                                            <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                                        ) : (
                                            <Trash2 className="h-4 w-4" />
                                        )}
                                    </Button>
                                </div>
                            )}
                            <CardHeader className="space-y-3">
                                <div className="flex items-start justify-between gap-4">
                                    <div className="flex items-center gap-2">
                                        <div>
                                            <CardTitle className="text-xl">{person.name || person.email}</CardTitle>
                                            <CardDescription>
                                                {person.slug} • {person.is_collective ? 'Collective' : PERSON_TYPE_META[person.type].label}
                                            </CardDescription>
                                        </div>
                                        {person.is_resident && person.type === 'artist' && (
                                            <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary">
                                                Resident
                                            </span>
                                        )}
                                    </div>
                                    <span className="rounded-full bg-secondary px-2 py-0.5 text-xs font-semibold uppercase tracking-wide text-secondary-foreground">
                                        {person.is_collective ? 'collective' : person.type}
                                    </span>
                                </div>
                                {summary.missingFields.length > 0 ? (
                                    <div className="flex flex-wrap gap-1">
                                        {summary.missingFields.map((field) => (
                                            <span key={field} className="inline-flex items-center gap-1 rounded-full bg-warning-muted px-2 py-0.5 text-xs text-warning-foreground">
                                                <AlertTriangle className="h-3 w-3" />
                                                {field} missing
                                            </span>
                                        ))}
                                    </div>
                                ) : (
                                    <span className="inline-flex items-center gap-1 rounded-full bg-success-muted px-2 py-0.5 text-xs text-success-muted-foreground">
                                        <Sparkles className="h-3 w-3" />
                                        Artist ready
                                    </span>
                                )}
                            </CardHeader>
                            <CardContent className="space-y-3">
                                {person.email && <p className="text-sm font-medium">{person.email}</p>}
                                <div className="space-y-1 text-sm text-muted-foreground">
                                    {person.pronouns && <p>Pronouns · {person.pronouns}</p>}
                                    {person.phone && <p>Phone · {person.phone}</p>}
                                </div>
                                {person.bio && <p className="text-sm text-muted-foreground line-clamp-3">{person.bio}</p>}

                                {/* Same set the table's invite column covers: staff and support
                                    get a timetable link instead, further down. */}
                                {(person.type === 'artist' || person.type === 'guest') && (
                                    <InviteLinkButton
                                        person={person}
                                        happening={counterpartShowFor(person.id)}
                                        appearance="card"
                                    />
                                )}

                                {(person.type === 'staff' || person.type === 'support') && (
                                    <button
                                        type="button"
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            handleCopyStaffLink(person);
                                        }}
                                        className="inline-flex w-full items-center justify-between rounded-md border border-dashed border-primary/40 bg-primary/5 px-3 py-2 text-left text-sm text-primary transition hover:bg-primary/10"
                                    >
                                        <span className="flex items-center gap-2">
                                            <Calendar className="h-4 w-4" />
                                            {copiedStaffLinkPersonId === person.id ? 'Timetable link copied' : 'Copy timetable link'}
                                        </span>
                                    </button>
                                )}
                            </CardContent>
                        </Card>
                    ))}
                </div>
            ) : (
                <div className="rounded-md border">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                {canDelete && (
                                    <TableHead className="w-12">
                                        <input
                                            type="checkbox"
                                            checked={selectedPersonIds.size === filteredPeople.length && filteredPeople.length > 0}
                                            onChange={toggleSelectAll}
                                            className="h-4 w-4 rounded border-gray-300"
                                        />
                                    </TableHead>
                                )}
                                <TableHead>Name</TableHead>
                                <TableHead>Type</TableHead>
                                <TableHead>Email</TableHead>
                                <TableHead>Pronouns</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead>Invite</TableHead>
                                {canDelete && <TableHead className="w-12">Actions</TableHead>}
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {peopleWithSummaries.map(({ person, summary }) => (
                                <TableRow
                                    key={person.id}
                                    className="cursor-pointer"
                                    onClick={() => openEditFlow(person)}
                                >
                                    {canDelete && (
                                        <TableCell onClick={(e) => e.stopPropagation()}>
                                            <input
                                                type="checkbox"
                                                checked={selectedPersonIds.has(person.id)}
                                                onChange={() => togglePersonSelection(person.id)}
                                                className="h-4 w-4 rounded border-gray-300"
                                            />
                                        </TableCell>
                                    )}
                                    <TableCell className="font-medium">
                                        <div>
                                            <p>{person.name || person.email}</p>
                                            <p className="text-xs text-muted-foreground">{person.slug}</p>
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        <span className="rounded-full bg-secondary text-secondary-foreground px-2 py-0.5 text-xs font-semibold uppercase">
                                            {person.is_collective ? 'collective' : person.type}
                                        </span>
                                    </TableCell>
                                    <TableCell className="text-muted-foreground">
                                        {person.email || '—'}
                                    </TableCell>
                                    <TableCell className="text-muted-foreground">
                                        {person.pronouns || '—'}
                                    </TableCell>
                                    <TableCell>
                                        {summary.missingFields.length > 0 ? (
                                            <span className="inline-flex items-center gap-1 rounded-full bg-warning-muted px-2 py-0.5 text-xs text-warning-foreground">
                                                <AlertTriangle className="h-3 w-3" />
                                                {summary.missingFields.length} missing
                                            </span>
                                        ) : (
                                            <span className="inline-flex items-center gap-1 rounded-full bg-success-muted px-2 py-0.5 text-xs text-success-muted-foreground">
                                                <Sparkles className="h-3 w-3" />
                                                Ready
                                            </span>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {person.type === 'staff' || person.type === 'support' ? (
                                            <button
                                                type="button"
                                                onClick={(event) => {
                                                    event.stopPropagation();
                                                    handleCopyStaffLink(person);
                                                }}
                                                className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
                                            >
                                                <Calendar className="h-3 w-3" />
                                                {copiedStaffLinkPersonId === person.id ? 'Copied!' : 'Timetable link'}
                                            </button>
                                        ) : (
                                            <InviteLinkButton
                                                person={person}
                                                happening={counterpartShowFor(person.id)}
                                                appearance="inline"
                                            />
                                        )}
                                    </TableCell>
                                    {canDelete && (
                                        <TableCell onClick={(e) => e.stopPropagation()}>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={(e) => handleDeleteClick(person, e)}
                                                className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                                                disabled={checkingSingleDependencies.has(person.id)}
                                                title={checkingSingleDependencies.has(person.id) ? 'Checking dependencies...' : 'Delete'}
                                            >
                                                {checkingSingleDependencies.has(person.id) ? (
                                                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                                                ) : (
                                                    <Trash2 className="h-4 w-4" />
                                                )}
                                            </Button>
                                        </TableCell>
                                    )}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            )}

            <Dialog open={dialogOpen} onOpenChange={(open) => (open ? setDialogOpen(true) : closeDialog())}>
                <DialogContent size="large" className="max-h-[90vh] overflow-y-auto">
                    {dialogStep === 'type' ? (
                        <div className="space-y-6">
                            <DialogHeader>
                                <DialogTitle>Who are you adding?</DialogTitle>
                                <DialogDescription>Pick the type so we can tailor the form and validation.</DialogDescription>
                            </DialogHeader>
                            <div className="px-4 sm:px-6">
                            <div className="grid gap-3 sm:grid-cols-2">
                                {(Object.keys(PERSON_TYPE_META) as PersonType[]).map((type) => {
                                    const meta = PERSON_TYPE_META[type];
                                    const isSelected = selectedType === type;
                                    return (
                                        <button
                                            type="button"
                                            key={type}
                                            onClick={() => setSelectedType(type)}
                                            className={`rounded-lg border p-4 text-left transition ${isSelected ? 'border-primary bg-primary/5' : 'border-input hover:border-primary/60'
                                                }`}
                                        >
                                            <div className="flex items-center justify-between">
                                                <p className="text-base font-semibold">{meta.label}</p>
                                                {isSelected && <Sparkles className="h-4 w-4 text-primary" />}
                                            </div>
                                            <p className="mt-2 text-sm text-muted-foreground">{meta.description}</p>
                                        </button>
                                    );
                                })}
                            </div>
                            </div>
                            <DialogFooter>
                                <Button onClick={handleTypeContinue}>
                                    Continue
                                    <ArrowRight className="ml-2 h-4 w-4" />
                                </Button>
                            </DialogFooter>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit} className="space-y-6">
                            <DialogHeader>
                                <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground">
                                    <span className="rounded-full bg-primary/10 px-2 py-0.5 text-primary">{typeMeta.label}</span>
                                    {editingPerson ? 'Editing existing person' : 'Creating new person'}
                                </div>
                                <div className="flex items-center gap-2">
                                    <DialogTitle>{editingPerson ? `Edit ${editingPerson.name}` : `Add ${typeMeta.label}`}</DialogTitle>
                                    {editingPerson && (
                                        <button
                                            type="button"
                                            onClick={async () => {
                                                const url = `${window.location.origin}/people?edit=${editingPerson.id}`;
                                                await navigator.clipboard.writeText(url);
                                                setCopiedInvitePersonId(`link-${editingPerson.id}`);
                                                setTimeout(() => setCopiedInvitePersonId(null), 4000);
                                            }}
                                            className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                                            title="Copy internal admin link — do NOT share with artists"
                                        >
                                            {copiedInvitePersonId === `link-${editingPerson.id}` ? (
                                                <span className="text-xs text-amber-600">Copied — admin-only link, do not share with artists!</span>
                                            ) : (
                                                <>
                                                    <Lock className="h-3.5 w-3.5" />
                                                    <span>Internal link</span>
                                                </>
                                            )}
                                        </button>
                                    )}
                                    {editingPerson && (
                                        <InviteLinkButton
                                            person={editingPerson}
                                            happening={dialogCounterpartShow}
                                            alternates={dialogShowAlternates}
                                            appearance="inline"
                                        />
                                    )}
                                </div>
                                <DialogDescription>{typeMeta.description}</DialogDescription>
                            </DialogHeader>

                            <div className="space-y-6 px-4 sm:px-6">
                            <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-dashed border-input px-4 py-3 text-sm text-muted-foreground">
                                <div className="space-y-1">
                                    <p className="font-medium text-foreground">{typeMeta.helper}</p>
                                    <p>Admins can save with only an email. Artists will see required fields when they log in.</p>
                                </div>
                                <Button type="button" variant="ghost" size="sm" onClick={() => setDialogStep('type')}>
                                    <ArrowLeft className="mr-2 h-3.5 w-3.5" />
                                    Change type
                                </Button>
                            </div>

                            {/* Artist Category Selector - Only for artists */}
                            {isArtist && (
                                <div className="space-y-3">
                                    <h3 className="text-lg font-semibold text-foreground border-l-4 border-primary pl-3">
                                        Artist Category
                                    </h3>
                                    <div className="grid gap-3 sm:grid-cols-2">
                                        {ARTIST_CATEGORY_OPTIONS.map((option) => {
                                            const isSelected = formState.isCollective === option.value;
                                            return (
                                                <button
                                                    type="button"
                                                    key={String(option.value)}
                                                    onClick={() => setFormState((prev) => ({ ...prev, isCollective: option.value }))}
                                                    className={`rounded-lg border p-4 text-left transition ${
                                                        isSelected ? 'border-primary bg-primary/5' : 'border-input hover:border-primary/60'
                                                    }`}
                                                >
                                                    <div className="flex items-center justify-between">
                                                        <p className="text-base font-semibold">{option.label}</p>
                                                        {isSelected && <Sparkles className="h-4 w-4 text-primary" />}
                                                    </div>
                                                    <p className="mt-2 text-sm text-muted-foreground">{option.description}</p>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {/* Basic Information Section */}
                            <div className="space-y-4">
                                <h3 className="text-lg font-semibold text-foreground border-l-4 border-primary pl-3">
                                    Basic Information
                                </h3>
                                <div className="grid gap-4 sm:grid-cols-2">
                                    <div className="space-y-1">
                                        <label className="text-sm font-medium">
                                            {isArtist && formState.isCollective ? 'Collective name' : 'Name'} (optional)
                                        </label>
                                    <input
                                        value={formState.name}
                                        onChange={handleFieldChange('name')}
                                        className="w-full rounded-md border border-input bg-background px-3 py-2"
                                        placeholder={isArtist && formState.isCollective ? 'Collective / crew name' : typeMeta.namePlaceholder}
                                    />
                                </div>
                                <div className="space-y-1">
                                    <div className="flex items-center justify-between text-sm font-medium">
                                        <label>Slug</label>
                                        {editingPerson ? (
                                            <button
                                                type="button"
                                                onClick={handleRegenerateSlug}
                                                className="text-xs text-primary hover:underline"
                                            >
                                                Regenerate from name
                                            </button>
                                        ) : (
                                            <span className="text-xs text-muted-foreground">Auto-generated</span>
                                        )}
                                    </div>
                                    <input
                                        value={formState.slug}
                                        onChange={handleFieldChange('slug')}
                                        className="w-full rounded-md border border-input bg-background px-3 py-2"
                                        placeholder="artist-name"
                                    />
                                    <p className="text-xs text-muted-foreground">{typeMeta.slugHint}</p>
                                </div>
                                </div>
                            </div>

                            {/* Contact Section */}
                            <div className="space-y-4">
                                <h3 className="text-lg font-semibold text-foreground border-l-4 border-primary pl-3">
                                    Contact
                                </h3>
                                <div className="grid gap-4 sm:grid-cols-2">
                                    <div className="space-y-1">
                                        <label className="text-sm font-medium">Pronouns</label>
                                    <input
                                        value={formState.pronouns}
                                        onChange={handleFieldChange('pronouns')}
                                        className="w-full rounded-md border border-input bg-background px-3 py-2"
                                        placeholder="she / they / he"
                                    />
                                </div>
                                <div className="space-y-1">
                                    <label className="text-sm font-medium">Email</label>
                                    <input
                                        type="email"
                                        required
                                        value={formState.email}
                                        onChange={handleFieldChange('email')}
                                        className="w-full rounded-md border border-input bg-background px-3 py-2"
                                        placeholder={isArtist ? 'artist@email.com' : 'Contact email'}
                                    />
                                </div>
                            </div>

                            <div className="grid gap-4 sm:grid-cols-2">
                                <div className="space-y-1">
                                    <label className="text-sm font-medium">Phone number</label>
                                    <PhoneInput
                                        value={formState.phone}
                                        onChange={(value) => setFormState(prev => ({ ...prev, phone: value }))}
                                        placeholder="Enter phone number"
                                        defaultCountry={defaultCountry}
                                    />
                                </div>
                                <PhotoUploadField
                                    label={isArtist ? (formState.isCollective ? 'Collective photo' : 'Artist photo') : 'Photo'}
                                    helper={
                                        isArtist
                                            ? `Prefer ${preferredImageRatio === 'portrait' ? 'portrait (3:4)' : 'landscape (3:2)'}. ${preferredImageRatio === 'portrait' ? 'Landscape photos crop in the center, so sides may be cut off.' : 'Portrait photos crop in the center, so heads may disappear.'}`
                                            : 'Optional promo photo for internal use.'
                                    }
                                    value={formState.pictureUrl}
                                    placeholder="https://"
                                    onChange={(url) =>
                                        setFormState((prev) => ({
                                            ...prev,
                                            pictureUrl: url,
                                        }))
                                    }
                                    category="artist_photo"
                                    identifier={photoIdentifier}
                                    imageFocus={formState.imageFocus}
                                    allowBypassSizeLimit
                                    preferredRatio={preferredImageRatio}
                                    onOrientationMismatch={({ mismatched }) => setImageMismatched(mismatched)}
                                    compact
                                />
                                {formState.pictureUrl && imageMismatched && (
                                    <ImageFocusSelect
                                        value={formState.imageFocus}
                                        onChange={(val) => setFormState((prev) => ({ ...prev, imageFocus: val }))}
                                    />
                                )}
                                </div>
                            </div>

                            {/* Bio Section */}
                            <div className="space-y-4">
                                <h3 className="text-lg font-semibold text-foreground border-l-4 border-primary pl-3">
                                    {isArtist ? (formState.isCollective ? 'Collective Bio' : 'Artist Bio') : 'Notes'}
                                </h3>
                                <div className="space-y-2">
                                    <div className="flex items-center justify-between">
                                        <label className="text-sm font-medium">
                                            {isArtist ? (formState.isCollective ? 'Collective bio' : 'Bio content') : 'Notes content'}
                                        </label>
                                        <button
                                            type="button"
                                            onClick={() => setShowMarkdownGuide(!showMarkdownGuide)}
                                            className="text-xs text-muted-foreground hover:text-foreground"
                                        >
                                            Markdown supported
                                        </button>
                                    </div>
                                    <textarea
                                        value={formState.bio}
                                        onChange={handleFieldChange('bio')}
                                        rows={6}
                                        className="w-full rounded-md border border-input bg-background px-3 py-2 font-mono text-sm"
                                        placeholder="**Bold**, *italic*, [links](url), etc."
                                    />
                                    {showMarkdownGuide && <MarkdownGuide />}
                                    {isArtist && (
                                        <p className="text-xs text-muted-foreground">
                                            Required for {formState.isCollective ? 'collectives' : 'artists'} when they complete their form. Markdown formatting supported.
                                        </p>
                                    )}
                                </div>
                            </div>

                            {/* Assigned Staff (Participants) - Required for artists */}
                            {isArtist && (
                                <div className={`space-y-3 mb-6 rounded-lg border p-4 ${displayParticipants.length === 0 ? 'border-destructive bg-destructive/5' : 'border-input bg-muted/30'}`}>
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <label className="text-sm font-medium flex items-center gap-2">
                                                <Users className="h-4 w-4" />
                                                Assigned Staff
                                                <span className="text-destructive">*</span>
                                            </label>
                                            <p className="text-xs text-muted-foreground mt-1">
                                                Assign at least one staff member as contact person for this artist
                                            </p>
                                        </div>
                                    </div>

                                    <div className="space-y-2">
                                        {displayParticipants.map((participant) => (
                                            <div key={participant.id} className="flex items-center gap-2 p-2 bg-background rounded border">
                                                <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                                                    {participant.role.replace(/_/g, ' ')}
                                                </span>
                                                <span className="flex-1 text-sm">{participant.user_name}</span>
                                                <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => handleRemoveParticipant(participant.id)}
                                                    className="h-8 w-8 p-0"
                                                >
                                                    <X className="h-4 w-4" />
                                                </Button>
                                            </div>
                                        ))}

                                        {displayParticipants.length === 0 && (
                                            <p className="text-xs text-destructive text-center py-2">
                                                At least one staff member is required. Add a contact person below.
                                            </p>
                                        )}
                                    </div>

                                    <div className="flex gap-2">
                                        <select
                                            value={newParticipantUserId}
                                            onChange={(e) => setNewParticipantUserId(e.target.value)}
                                            className="flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm"
                                        >
                                            <option value="">Select staff member...</option>
                                            {allUsers.map((user) => (
                                                <option key={user.id} value={user.id}>{user.name}</option>
                                            ))}
                                        </select>
                                        <input
                                            type="text"
                                            value={newParticipantRole}
                                            onChange={(e) => setNewParticipantRole(e.target.value)}
                                            placeholder="Role (e.g., contact)"
                                            className="w-40 rounded-md border border-input bg-background px-3 py-2 text-sm"
                                        />
                                        <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            onClick={handleAddParticipant}
                                            disabled={!newParticipantUserId || !newParticipantRole.trim()}
                                        >
                                            <Plus className="h-4 w-4 mr-1" />
                                            Add
                                        </Button>
                                    </div>
                                </div>
                            )}

                            {isArtist && (
                                <div className="space-y-4">
                                    <div>
                                        <h3 className="text-lg font-semibold text-foreground border-l-4 border-primary pl-3">
                                            Socials
                                        </h3>
                                        <p className="text-xs text-muted-foreground mt-2 pl-4">Optional for admins. Artists can fill them in later.</p>
                                    </div>
                                    <div className="grid gap-3 sm:grid-cols-2">
                                        <div className="space-y-1">
                                            <input
                                                value={formState.instagram}
                                                onChange={handleFieldChange('instagram')}
                                                className={`w-full rounded-md border ${urlErrors.instagram ? 'border-red-500' : 'border-input'} bg-background px-3 py-2`}
                                                placeholder="Instagram URL (e.g., https://instagram.com/yourname)"
                                            />
                                            {urlErrors.instagram && (
                                                <p className="text-xs text-error">{urlErrors.instagram}</p>
                                            )}
                                        </div>
                                        <div className="space-y-1">
                                            <input
                                                value={formState.soundcloud}
                                                onChange={handleFieldChange('soundcloud')}
                                                className={`w-full rounded-md border ${urlErrors.soundcloud ? 'border-red-500' : 'border-input'} bg-background px-3 py-2`}
                                                placeholder="SoundCloud URL (e.g., https://soundcloud.com/yourname)"
                                            />
                                            {urlErrors.soundcloud && (
                                                <p className="text-xs text-error">{urlErrors.soundcloud}</p>
                                            )}
                                        </div>
                                    </div>
                                    <div className="grid gap-3 sm:grid-cols-2">
                                        <div className="space-y-1">
                                            <input
                                                value={formState.bandcamp}
                                                onChange={handleFieldChange('bandcamp')}
                                                className={`w-full rounded-md border ${urlErrors.bandcamp ? 'border-red-500' : 'border-input'} bg-background px-3 py-2`}
                                                placeholder="Bandcamp URL (e.g., https://yourname.bandcamp.com)"
                                            />
                                            {urlErrors.bandcamp && (
                                                <p className="text-xs text-error">{urlErrors.bandcamp}</p>
                                            )}
                                        </div>
                                        <div className="space-y-2 rounded-md border border-dashed border-input p-3">
                                            <p className="text-sm font-medium">Custom links</p>
                                            <div className="space-y-2">
                                                {formState.customSocials.map((social) => (
                                                    <div key={social.id} className="grid gap-2 sm:grid-cols-[1fr_auto]">
                                                        <input
                                                            value={social.label}
                                                            onChange={(event) => handleCustomSocialChange(social.id, 'label', event.target.value)}
                                                            className="rounded-md border border-input bg-background px-3 py-2"
                                                            placeholder="Label (e.g. Mixcloud)"
                                                        />
                                                        <div className="flex gap-2">
                                                            <div className="flex-1 space-y-1">
                                                                <input
                                                                    value={social.url}
                                                                    onChange={(event) => handleCustomSocialChange(social.id, 'url', event.target.value)}
                                                                    className={`w-full rounded-md border ${urlErrors[`custom_${social.id}`] ? 'border-red-500' : 'border-input'} bg-background px-3 py-2`}
                                                                    placeholder="https://example.com"
                                                                />
                                                                {urlErrors[`custom_${social.id}`] && (
                                                                    <p className="text-xs text-error">{urlErrors[`custom_${social.id}`]}</p>
                                                                )}
                                                            </div>
                                                            <Button
                                                                type="button"
                                                                variant="ghost"
                                                                size="icon"
                                                                onClick={() => removeCustomSocialRow(social.id)}
                                                                className="shrink-0"
                                                            >
                                                                <span aria-hidden>×</span>
                                                                <span className="sr-only">Remove custom link</span>
                                                            </Button>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                            <Button type="button" variant="outline" size="sm" onClick={addCustomSocialRow}>
                                                Add link
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Related Happenings - Backlinks */}
                            {editingPerson && (
                                <div className="space-y-4">
                                    <div className="flex items-center gap-3">
                                        <h3 className="text-lg font-semibold text-foreground border-l-4 border-primary pl-3">
                                            Related Happenings
                                        </h3>
                                        <span className="text-sm text-muted-foreground">
                                            ({personHappenings.length})
                                        </span>
                                    </div>
                                    <div className="rounded-md border border-input p-4">
                                    {personHappenings.length === 0 ? (
                                        <p className="text-sm text-muted-foreground">
                                            No happenings yet. This person will appear here when added to shows.
                                        </p>
                                    ) : (
                                        <div className="space-y-2 max-h-48 overflow-y-auto">
                                            {personHappenings.map((happening) => (
                                                <div
                                                    key={happening.id}
                                                    className="flex items-center justify-between rounded-md border border-dashed bg-muted/30 px-3 py-2 text-sm"
                                                >
                                                    <div className="flex items-center gap-3">
                                                        {happening.image_url && (
                                                            <img
                                                                src={happening.image_url}
                                                                alt=""
                                                                className="h-8 w-8 rounded object-cover"
                                                            />
                                                        )}
                                                        <div>
                                                            <p className="font-medium">{happening.title}</p>
                                                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                                                <span className="capitalize">{happening.type}</span>
                                                                {happening.start_time && (
                                                                    <>
                                                                        <span>•</span>
                                                                        <span className="flex items-center gap-1">
                                                                            <Calendar className="h-3 w-3" />
                                                                            {new Date(happening.start_time).toLocaleDateString()}
                                                                        </span>
                                                                    </>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <span
                                                        className={`rounded-full px-2 py-0.5 text-xs font-medium ${happening.status === 'confirmed'
                                                            ? 'bg-emerald-100 text-emerald-900'
                                                            : happening.status === 'scheduled'
                                                                ? 'bg-blue-100 text-blue-900'
                                                                : 'bg-secondary text-secondary-foreground'
                                                            }`}
                                                    >
                                                        {happening.status}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    </div>
                                </div>
                            )}

                            {/* Staffing Section (staff/support only) */}
                            {isStaffType && (
                                <div className="space-y-4">
                                    <h3 className="text-lg font-semibold text-foreground border-l-4 border-primary pl-3">
                                        Staffing
                                    </h3>
                                    <div className="space-y-3 rounded-lg border border-input bg-muted/30 p-4">
                                        <div>
                                            <label className="text-sm font-medium">Roles this person can cover</label>
                                            <p className="text-xs text-muted-foreground mb-2">
                                                Used to match them to shifts. Manage the list under Settings → Staff Roles.
                                            </p>
                                            <RoleMultiSelect value={staffRoleIds} onChange={setStaffRoleIds} />
                                        </div>
                                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                                            <div className="space-y-1">
                                                <label className="text-sm font-medium">Max consecutive hours</label>
                                                <input
                                                    type="number"
                                                    min={0}
                                                    value={staffConstraints.maxConsecutive}
                                                    onChange={(e) => setStaffConstraints((prev) => ({ ...prev, maxConsecutive: e.target.value }))}
                                                    placeholder="e.g. 6"
                                                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                                />
                                            </div>
                                            <div className="space-y-1">
                                                <label className="text-sm font-medium">Max daily hours</label>
                                                <input
                                                    type="number"
                                                    min={0}
                                                    value={staffConstraints.maxDaily}
                                                    onChange={(e) => setStaffConstraints((prev) => ({ ...prev, maxDaily: e.target.value }))}
                                                    placeholder="optional"
                                                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                                />
                                            </div>
                                            <div className="space-y-1">
                                                <label className="text-sm font-medium">Min break (minutes)</label>
                                                <input
                                                    type="number"
                                                    min={0}
                                                    value={staffConstraints.minBreak}
                                                    onChange={(e) => setStaffConstraints((prev) => ({ ...prev, minBreak: e.target.value }))}
                                                    placeholder="optional"
                                                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                                />
                                            </div>
                                        </div>
                                        <div className="space-y-2 border-t border-input pt-3">
                                            <label className="text-sm font-medium">Restrict to locations</label>
                                            <p className="text-xs text-muted-foreground">
                                                If set, this person is only assigned to shifts at the chosen locations. Leave empty for no restriction.
                                            </p>
                                            <PlaceMultiSelect value={allowedPlaceIds} onChange={setAllowedPlaceIds} />
                                        </div>
                                        <div className="space-y-2 border-t border-input pt-3">
                                            <label className="text-sm font-medium">Availability</label>
                                            <p className="text-xs text-muted-foreground">
                                                Days (or hour ranges) this person is available to be assigned. Use "All day"
                                                to mark a whole day, or uncheck it to set specific hours.
                                            </p>
                                            <AvailabilityEditor value={availabilityWindows} onChange={setAvailabilityWindows} />
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Reminders & Notifications Section */}
                            {isArtist && (
                                <div className="space-y-4">
                                    <h3 className="text-lg font-semibold text-foreground border-l-4 border-primary pl-3">
                                        Reminders & Notifications
                                    </h3>

                                    {/* Completion Reminders - Only show for artists with email */}
                                    {formState.email && (
                                        <div className="space-y-3 rounded-lg border border-input bg-muted/30 p-4">
                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <label className="text-sm font-medium flex items-center gap-2">
                                                        <Mail className="h-4 w-4" />
                                                        Completion Reminders
                                                    </label>
                                                    <p className="text-xs text-muted-foreground mt-1">
                                                        Email reminders will be sent to {formState.email} when the profile is incomplete
                                                    </p>
                                                </div>
                                            </div>

                                            <div className="space-y-2">
                                                {completionReminders.map((reminder, index) => {
                                                    const absHours = Math.abs(reminder.offset_hours);
                                                    // For profiles, offset_hours is positive (hours after creation)
                                                    const offsetDesc = reminder.offset_hours === 0
                                                        ? 'Immediately'
                                                        : reminder.offset_hours < 24
                                                            ? `${absHours} hour${absHours !== 1 ? 's' : ''} after creation`
                                                            : reminder.offset_hours < 168
                                                                ? `${Math.floor(absHours / 24)} day${Math.floor(absHours / 24) !== 1 ? 's' : ''} after creation`
                                                                : `${Math.floor(absHours / 168)} week${Math.floor(absHours / 168) !== 1 ? 's' : ''} after creation`;

                                                    return (
                                                        <div key={index} className="flex items-center gap-2 p-2 bg-background rounded border">
                                                            <Clock className="h-4 w-4 text-muted-foreground" />
                                                            <span className="flex-1 text-sm">{offsetDesc}</span>
                                                            {existingCompletionReminders.find(r => r.id === reminder.id)?.sent_at && (
                                                                <span className="text-xs text-muted-foreground">
                                                                    Sent: {new Date(existingCompletionReminders.find(r => r.id === reminder.id)!.sent_at!).toLocaleString()}
                                                                </span>
                                                            )}
                                                            <Button
                                                                type="button"
                                                                variant="ghost"
                                                                size="sm"
                                                                onClick={() => {
                                                                    setCompletionReminders(completionReminders.filter((_, i) => i !== index));
                                                                }}
                                                                className="h-8 w-8 p-0"
                                                            >
                                                                <X className="h-4 w-4" />
                                                            </Button>
                                                        </div>
                                                    );
                                                })}

                                                {completionReminders.length === 0 && (
                                                    <p className="text-xs text-muted-foreground text-center py-2">
                                                        No reminders configured. Click "Add Reminder" to add one.
                                                    </p>
                                                )}
                                            </div>

                                            <div className="flex gap-2">
                                                <select
                                                    id="new-profile-completion-reminder-offset"
                                                    className="flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm"
                                                    defaultValue="24"
                                                >
                                                    <option value="0">Immediately</option>
                                                    <option value="24">1 day after creation</option>
                                                    <option value="48">2 days after creation</option>
                                                    <option value="72">3 days after creation</option>
                                                    <option value="168">1 week after creation</option>
                                                </select>
                                                <Button
                                                    type="button"
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => {
                                                        const select = document.getElementById('new-profile-completion-reminder-offset') as HTMLSelectElement;
                                                        const offsetHours = parseInt(select.value, 10);
                                                        // Check if this offset already exists
                                                        if (!completionReminders.some(r => r.offset_hours === offsetHours)) {
                                                            setCompletionReminders([...completionReminders, { offset_hours: offsetHours }]);
                                                        }
                                                    }}
                                                >
                                                    <Plus className="h-4 w-4 mr-1" />
                                                    Add Reminder
                                                </Button>
                                            </div>
                                        </div>
                                    )}

                                    {/* Completion Notifications - Required when there are participants */}
                                    {uniqueParticipantRoles.length > 0 && (
                                        <div className={`space-y-3 rounded-lg border p-4 ${notifyRoles.length === 0 ? 'border-destructive bg-destructive/5' : 'border-input bg-muted/30'}`}>
                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <label className="text-sm font-medium flex items-center gap-2">
                                                        <Bell className="h-4 w-4" />
                                                        Completion Notifications
                                                        <span className="text-destructive">*</span>
                                                    </label>
                                                    <p className="text-xs text-muted-foreground mt-1">
                                                        Select at least one role to notify when this artist completes their profile
                                                    </p>
                                                </div>
                                            </div>

                                            <div className="flex flex-wrap gap-2">
                                                {uniqueParticipantRoles.map((role) => (
                                                    <label key={role} className="flex items-center gap-2 p-2 bg-background rounded border cursor-pointer hover:bg-muted/50">
                                                        <input
                                                            type="checkbox"
                                                            checked={notifyRoles.includes(role)}
                                                            onChange={(e) => {
                                                                if (e.target.checked) {
                                                                    setNotifyRoles([...notifyRoles, role]);
                                                                } else {
                                                                    setNotifyRoles(notifyRoles.filter(r => r !== role));
                                                                }
                                                            }}
                                                            className="rounded"
                                                        />
                                                        <span className="text-sm capitalize">{role.replace(/_/g, ' ')}</span>
                                                    </label>
                                                ))}
                                            </div>

                                            {notifyRoles.length === 0 && (
                                                <p className="text-xs text-destructive">
                                                    At least one notification role is required.
                                                </p>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Settings Section */}
                            {selectedType === 'artist' && (
                                <div className="space-y-4">
                                    <h3 className="text-lg font-semibold text-foreground border-l-4 border-primary pl-3">
                                        Settings
                                    </h3>
                                    <div className="space-y-3">
                                        <div className="flex items-start gap-3 rounded-md border border-input bg-muted/30 px-4 py-4">
                                            <input
                                                id="is-resident"
                                                type="checkbox"
                                                className="mt-1"
                                                checked={formState.isResident}
                                                onChange={(e) => setFormState(prev => ({ ...prev, isResident: e.target.checked }))}
                                            />
                                            <div className="space-y-1 text-sm">
                                                <label htmlFor="is-resident" className="font-medium">
                                                    Resident artist
                                                </label>
                                                <p className="text-muted-foreground">
                                                    Mark this artist as a resident for recurring shows and template defaults.
                                                </p>
                                            </div>
                                        </div>

                                        <div className="flex items-start gap-3 rounded-md border border-input bg-muted/30 px-4 py-4">
                                            <input
                                                id="is-public"
                                                type="checkbox"
                                                className="mt-1"
                                                checked={formState.isPublic}
                                                onChange={(e) => setFormState(prev => ({ ...prev, isPublic: e.target.checked }))}
                                            />
                                            <div className="space-y-1 text-sm">
                                                <label htmlFor="is-public" className="font-medium">
                                                    Public artist
                                                </label>
                                                <p className="text-muted-foreground">
                                                    Make this artist visible on the public website. Private artists are only visible in the admin.
                                                </p>
                                            </div>
                                        </div>

                                        <div className="flex items-start gap-3 rounded-md border border-input bg-muted/30 px-4 py-4">
                                            <input
                                                id="invite-link"
                                                type="checkbox"
                                                className="mt-1"
                                                checked={formState.inviteLinkRequested}
                                                onChange={handleInviteToggle}
                                            />
                                            <div className="space-y-1 text-sm">
                                                <label htmlFor="invite-link" className="font-medium">
                                                    {editingPerson && editingPerson.invite_token
                                                        ? 'Regenerate invite link'
                                                        : 'Generate artist-facing link'}
                                                </label>
                                                <p className="text-muted-foreground">
                                                    {editingPerson && editingPerson.invite_token
                                                        ? 'Generate a new invite link. This will invalidate any previously sent links.'
                                                        : "We'll create a locked-down link so the artist can finish their required fields themselves."}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {mutationError && (
                                <p className="text-sm font-medium text-destructive">{mutationError.message}</p>
                            )}

                            {editingPerson && canDelete && (
                                <DangerZone
                                    entityLabel={editingPerson.type === 'artist' ? 'artist' : 'person'}
                                    onDelete={() => handleDeleteClick(editingPerson)}
                                    disabled={deleteMutation.isPending}
                                />
                            )}
                            </div>

                            <DialogFooter className="gap-2">
                                <Button type="button" variant="outline" onClick={closeDialog}>
                                    Cancel
                                </Button>
                                <Button
                                    type="submit"
                                    disabled={isSaving || (selectedType === 'artist' && (displayParticipants.length === 0 || notifyRoles.length === 0))}
                                >
                                    {isSaving ? 'Saving…' : editingPerson ? 'Save changes' : 'Create person'}
                                </Button>
                            </DialogFooter>
                        </form>
                    )}
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            {deletingPerson && (
                <DeleteConfirmationDialog
                    open={deleteDialogOpen}
                    onOpenChange={setDeleteDialogOpen}
                    onConfirm={handleDeleteConfirm}
                    entityName={deletingPerson.name}
                    entityType="person"
                    dependencies={deleteDependencies || undefined}
                    isDeleting={deleteMutation.isPending}
                    mediaUrls={deletingPerson.picture_url ? [deletingPerson.picture_url] : []}
                />
            )}

            {/* Bulk Delete Confirmation Dialog */}
            {selectedPersonIds.size > 0 && (
                <DeleteConfirmationDialog
                    open={bulkDeleteDialogOpen}
                    onOpenChange={setBulkDeleteDialogOpen}
                    onConfirm={handleBulkDeleteConfirm}
                    entityName=""
                    entityType="person"
                    entityCount={selectedPersonIds.size}
                    dependencies={(() => {
                        // Aggregate dependencies from all selected people
                        const allDeps: DependencyCheckResult = {
                            hasDependencies: false,
                            dependencies: [],
                        };
                        const depMap = new Map<string, number>();
                        const depItems = new Map<string, Array<{ id: string; name: string }>>();

                        bulkDeleteDependencies.forEach((result) => {
                            if (result.hasDependencies) {
                                allDeps.hasDependencies = true;
                                result.dependencies.forEach((dep) => {
                                    const key = dep.type;
                                    depMap.set(key, (depMap.get(key) || 0) + dep.count);
                                    if (!depItems.has(key)) {
                                        depItems.set(key, []);
                                    }
                                    depItems.get(key)!.push(...dep.items);
                                });
                            }
                        });

                        depMap.forEach((count, type) => {
                            allDeps.dependencies.push({
                                type,
                                count,
                                items: depItems.get(type) || [],
                            });
                        });

                        return allDeps;
                    })()}
                    isDeleting={bulkDeleteMutation.isPending}
                    mediaUrls={(() => {
                        // Collect all picture URLs from selected people
                        const urls: string[] = [];
                        const selectedIds = Array.from(selectedPersonIds);
                        people.forEach((p) => {
                            if (selectedIds.includes(p.id) && p.picture_url) {
                                urls.push(p.picture_url);
                            }
                        });
                        return urls;
                    })()}
                />
            )}
        </div>
    );
}
